import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X as XIcon, Save, Upload, Trash2, Play } from "lucide-react";
import { api, formatError, resolveAsset } from "../lib/api";

// Convert a stored UTC ISO string → "YYYY-MM-DDTHH:MM" (viewer local tz),
// which is the value format <input type="datetime-local"> expects.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Convert "YYYY-MM-DDTHH:MM" (viewer local tz) → UTC ISO string.
function localInputToIso(val) {
  if (!val) return null;
  const d = new Date(val); // interpreted as local time
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function EditTournamentModal({ tournament, onClose, onSaved, onDeleted, teams = [] }) {
  const [form, setForm] = useState({
    name: tournament.name || "",
    description: tournament.description || "",
    format: tournament.format || "single_elimination",
    prize_pool: tournament.prize_pool || 0,
    max_teams: tournament.max_teams || 32,
    game: tournament.game || "",
    host: tournament.host || "",
    status: tournament.status || "scheduled",
    mvp: tournament.mvp || "",
    winner_team_id: tournament.winner_team_id || "",
    runner_up_team_id: tournament.runner_up_team_id || "",
    ended_at: tournament.ended_at || "",
    banner_url: tournament.banner_url || "",
    // Convert stored ISO → `YYYY-MM-DDTHH:MM` expected by `<input type=datetime-local>`
    starts_at: tournament.starts_at ? isoToLocalInput(tournament.starts_at) : "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.prize_pool = Number(payload.prize_pool) || 0;
      payload.max_teams = Math.max(2, Math.min(32, Number(payload.max_teams) || 32));
      // Convert datetime-local → UTC ISO (or null when cleared)
      payload.starts_at = localInputToIso(form.starts_at);
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      const { data } = await api.put(`/tournaments/${tournament.id}`, payload);
      toast.success("Tournament updated");
      onSaved && onSaved(data);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const uploadBanner = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, banner_url: data.url }));
      toast.success("Banner uploaded — click Save to apply");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setUploading(false);
  };

  const del = async () => {
    if (!window.confirm(`Delete "${tournament.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tournaments/${tournament.id}`);
      toast.success("Tournament deleted");
      onDeleted && onDeleted(tournament.id);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const generate = async () => {
    if (!window.confirm("Generate (or regenerate) the bracket? Existing matches will be replaced.")) return;
    try {
      await api.post(`/tournaments/${tournament.id}/generate`);
      toast.success("Bracket generated");
      onSaved && onSaved(tournament);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-auto">
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-tournament-modal"
        className="glass-red-glow rounded-2xl max-w-2xl w-full p-8 relative my-8"
      >
        <button
          onClick={onClose}
          data-testid="edit-tournament-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
        >
          <XIcon size={14} />
        </button>

        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">Manage tournament</div>
        <h2 className="font-display font-black text-3xl tracking-tight mb-6">Edit {tournament.name}</h2>

        {/* Banner */}
        <div className="mb-5">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Banner</div>
          <div className="flex items-center gap-4">
            {form.banner_url ? (
              <img src={resolveAsset(form.banner_url)} alt="" className="h-16 w-28 rounded-lg object-cover border border-white/10" />
            ) : (
              <div className="h-16 w-28 rounded-lg border border-dashed border-white/10 flex items-center justify-center text-xs text-zinc-500">No banner</div>
            )}
            <label className="btn-ghost-glass !py-2 !px-4 !text-xs cursor-pointer">
              <Upload size={12} /> {uploading ? "Uploading..." : "Upload banner"}
              <input
                type="file" accept="image/*" className="hidden"
                disabled={uploading}
                data-testid="edit-tournament-banner"
                onChange={(e) => e.target.files?.[0] && uploadBanner(e.target.files[0])}
              />
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="edit-t-name" />
          <Field label="Game" value={form.game} onChange={(v) => setForm({ ...form, game: v })} tid="edit-t-game" />
          <Field label="Host" value={form.host} onChange={(v) => setForm({ ...form, host: v })} tid="edit-t-host" />
          <Field label="Prize Pool ($)" type="number" value={form.prize_pool} onChange={(v) => setForm({ ...form, prize_pool: v })} tid="edit-t-prize" />
          <Field label="Max teams (2-32)" type="number" value={form.max_teams} onChange={(v) => setForm({ ...form, max_teams: v })} tid="edit-t-max" />
          <Field label="Starts at (date & time)" type="datetime-local" value={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} tid="edit-t-starts" />
          <Select label="Format" value={form.format} onChange={(v) => setForm({ ...form, format: v })}
            options={[
              { value: "single_elimination", label: "Single Elimination" },
              { value: "double_elimination", label: "Double Elimination" },
              { value: "round_robin", label: "Round Robin" },
              { value: "group_advancement", label: "Group Advancement (Top 2)" },
            ]} tid="edit-t-format" />
          <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
            options={[
              { value: "draft", label: "Draft" },
              { value: "scheduled", label: "Scheduled" },
              { value: "live", label: "Live" },
              { value: "finished", label: "Finished" },
            ]} tid="edit-t-status" />
          <Field label="Ended at (ISO date)" value={form.ended_at} onChange={(v) => setForm({ ...form, ended_at: v })} tid="edit-t-ended" />
          <Field label="MVP" value={form.mvp} onChange={(v) => setForm({ ...form, mvp: v })} tid="edit-t-mvp" />
          <Select label="Champion" value={form.winner_team_id} onChange={(v) => setForm({ ...form, winner_team_id: v })}
            options={[{ value: "", label: "— none —" }, ...teams.map((t) => ({ value: t.id, label: t.name }))]}
            tid="edit-t-winner" />
          <Select label="Runner-up" value={form.runner_up_team_id} onChange={(v) => setForm({ ...form, runner_up_team_id: v })}
            options={[{ value: "", label: "— none —" }, ...teams.map((t) => ({ value: t.id, label: t.name }))]}
            tid="edit-t-runner" />
        </div>

        <label className="block mb-6">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Description</span>
          <textarea
            data-testid="edit-t-desc"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500 resize-none"
          />
        </label>

        <div className="flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2 flex-wrap">
            <button onClick={generate} data-testid="edit-t-generate" className="btn-ghost-glass !px-4">
              <Play size={14} /> Regenerate bracket
            </button>
            <button onClick={del} data-testid="edit-t-delete" className="btn-ghost-glass !px-4 hover:!text-red-400">
              <Trash2 size={14} /> Delete
            </button>
          </div>
          <button onClick={save} disabled={busy} data-testid="edit-t-save" className="btn-liquid !px-5">
            <Save size={14} /> {busy ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", tid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <input
        data-testid={tid} type={type} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, tid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <select
        data-testid={tid} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
